/*
 * PRECONFIGURED-RULES: the optional extras of a rim's `custom.pre_configured_wheel_rules` rule (release 1 of the LoamLabs preconfigured builds).
 * The 108 Reserve rules have none of these fields, so every function here answers "no extra condition" for them and they behave exactly as before.
 *
 * Optional rule fields:
 *   allowedHubVariantIds   [id, ...]  front and rear hub must be one of these variants (numeric id or gid)
 *   allowedSpokeVariantIds [id, ...]  front and rear spokes must be one of these variants
 *   hubColors / spokeColors [name, ...] the Color option of the chosen hub / spoke variant must be one of these (case-insensitive)
 *   frontWheelSize / rearWheelSize    the wheel size chosen in Step 2 must be this (tells a 29" set from a mullet that uses the same parts)
 *   valves: "extra"                   the master price excludes valve stems; kept valves are added as normal lines (default "included")
 *   freehubLine: true                the master price does NOT include a freehub body: the chosen body is added as its own cart line at its real price ($0 for
 *                                    XD / HG / XD-R / HG-R, e.g. $109.99 for a DT Swiss MS or N3W body) so its stock counts and a priced body is paid on top.
 *                                    The body is in the discount collection (tag component:freehub): LOAMLABS10 takes 10% off a priced body, like the other eligible lines,
 *                                    but only once the cart has 8 eligible items (default: no line, as for the Reserve rules)
 * A different colour means NO match, so the build is priced as separate parts with the normal discount.
 * A master variant that is not available for sale is skipped too (see priceFromNode).
 *
 * Pure functions, no DOM: tested in tests/preconfigured-rules.test.js.
 */
(function (global) {
  'use strict';

  // Switch for the freehub-body cart line (only for rules with freehubLine: true). Backend 1 confirmed the order webhook ignores that line.
  // discountMinItems: LOAMLABS10 only takes the 10% off when the cart holds at least this many qualifying items (Shopify's own setting, read-only here).
  var CFG = { freehubLine: true, discountPercent: 10, discountMinItems: 8 };

  function digits(v) { var s = String(v == null ? '' : v).replace(/\D/g, ''); return s || null; }
  function list(v) { return Array.isArray(v) ? v : []; }
  function optionValue(comp, name) {
    var opts = comp && comp.selectedOptions;
    if (!Array.isArray(opts)) return null;
    for (var i = 0; i < opts.length; i++) if (opts[i] && String(opts[i].name || '').toLowerCase() === name) return String(opts[i].value == null ? '' : opts[i].value);
    return null;
  }
  function colorOf(comp) { return optionValue(comp, 'color'); }
  function inIds(comp, ids) {
    var want = list(ids).map(digits).filter(Boolean);
    if (!want.length) return true; // empty list = no condition
    var have = digits(comp && (comp.variantId || comp.variantGid));
    return !!have && want.indexOf(have) >= 0;
  }
  function inColors(comp, names) {
    var want = list(names).map(function (n) { return String(n).trim().toLowerCase(); }).filter(Boolean);
    if (!want.length) return true;
    var have = colorOf(comp);
    return have != null && want.indexOf(have.trim().toLowerCase()) >= 0;
  }

  // components: the builder's buildState.components. studioFeeCents: StandardStudio.feeCents() (a custom-coloured wheel is not the master's colour).
  function extrasMatch(rule, components, studioFeeCents, specs) {
    if (!rule || !components) return true;
    if (rule.frontWheelSize && (!specs || String(specs.frontWheelSize) !== String(rule.frontWheelSize))) return false;
    if (rule.rearWheelSize && (!specs || String(specs.rearWheelSize) !== String(rule.rearWheelSize))) return false;
    var hasColourRule = !!(list(rule.allowedHubVariantIds).length || list(rule.allowedSpokeVariantIds).length || list(rule.hubColors).length || list(rule.spokeColors).length);
    if (!hasColourRule) return true;
    if (studioFeeCents > 0) return false;
    var fh = components.frontHub, rh = components.rearHub, fs = components.frontSpokes, rs = components.rearSpokes;
    return inIds(fh, rule.allowedHubVariantIds) && inIds(rh, rule.allowedHubVariantIds) &&
      inIds(fs, rule.allowedSpokeVariantIds) && inIds(rs, rule.allowedSpokeVariantIds) &&
      inColors(fh, rule.hubColors) && inColors(rh, rule.hubColors) &&
      inColors(fs, rule.spokeColors) && inColors(rs, rule.spokeColors);
  }

  // RELEASE 11: a build opened from a master's link (fullBuild.data.master) keeps that master's price only while its nipples are still the master's own (a nipple swap
  // is a change like a different spoke: separate parts at parts prices). Pure; true = no objection. It never objects for a build without a master link (hand-built,
  // System presets), for a rule that belongs to another master, or for a nipple the link does not name. fullBuildData: buildState.fullBuild.data.
  function nipplesMatchLink(rule, fullBuildData, components) {
    var master = digits(fullBuildData && fullBuildData.master);
    if (!master || !rule || digits(rule.masterVariantGid) !== master) return true;
    var parts = fullBuildData.parts || {};
    return ['frontNipples', 'rearNipples'].every(function (k) {
      var want = digits(parts[k]);
      if (!want) return true;
      var c = components && components[k];
      return digits(c && (c.variantId || c.variantGid)) === want;
    });
  }

  function valvesExtra(rule) { return !!rule && String(rule.valves || '').toLowerCase() === 'extra'; }

  // Storefront node { id, price:{amount}, availableForSale } -> price in cents, or null when the master cannot be sold (rule is skipped).
  function priceFromNode(node) {
    if (!node || !node.price) return null;
    if (node.availableForSale === false) return null; // undefined (an older query) keeps the old behaviour
    var n = parseFloat(node.price.amount);
    return isFinite(n) ? n * 100 : null;
  }

  // Valve stems the customer kept: [{ pos, cents }]. Included (free) and virtual valves do not count.
  function keptValves(components) {
    var out = [];
    ['front', 'rear'].forEach(function (pos) {
      var v = components && components[pos + 'ValveStem'];
      if (v && !v.isIncluded && !v.isVirtual && typeof v.price === 'number' && v.price > 0) out.push({ pos: pos, cents: v.price });
    });
    return out;
  }
  // Gross and discount for kept valves: the builder's own per-line rule (10% of each line, fraction of a cent dropped), but ONLY when the cart has
  // enough qualifying items. A preconfigured build is one master line that is not in the discount collection, so its valves are the only counted items
  // (plus any accessories): 2 valves are far below the minimum of 8 and are charged at full price. counted === undefined keeps the plain per-line rule.
  function valveTotals(components, percent, counted) {
    var p = (percent == null ? CFG.discountPercent : percent) / 100, gross = 0, disc = 0;
    var qualifies = counted === undefined || counted >= CFG.discountMinItems;
    keptValves(components).forEach(function (v) { gross += v.cents; if (qualifies) disc += Math.floor(v.cents * p); });
    return { grossCents: gross, discountCents: disc, netCents: gross - disc };
  }

  // The freehub body the customer chose, charged on top of the master price (cents). Freehub bodies are in the discount collection "Custom Wheel Components"
  // (tag rule component:freehub, added 2026-11-02), so a priced body gets the 10% like the other eligible lines, but ONLY when the cart reaches the minimum of 8
  // eligible items. counted = the eligible lines that will really be in the cart (see countedItems); without it no discount is promised.
  function freehubSurcharge(components, rule, counted) {
    var fh = components && components.rearComponentFreehub;
    if (!rule || rule.freehubLine !== true || !fh) return { cents: 0, discountCents: 0, netCents: 0, name: null, qualifies: false };
    var c = Math.round(parseFloat(fh.price)); // the builder keeps component prices in cents
    c = isFinite(c) && c > 0 ? c : 0;
    var qualifies = counted !== undefined && counted >= CFG.discountMinItems;
    var d = qualifies ? Math.floor(c * CFG.discountPercent / 100) : 0;
    return { cents: c, discountCents: d, netCents: c - d, name: fh.variantTitle || null, qualifies: qualifies };
  }
  // What the cart summary says about a priced freehub body. money = a function cents -> "$1,234.56".
  var FREEHUB_PENDING = '10% off the body once your cart has ' + CFG.discountMinItems + ' eligible items';
  function freehubText(masterCents, fh, counted, money) {
    if (!fh || !(fh.cents > 0)) return '';
    var head = 'Build ' + money(masterCents) + ' + ' + (fh.name ? fh.name + ' ' : '') + 'freehub body ' + money(fh.cents);
    return fh.qualifies ? head + ', 10% off the body (' + money(fh.netCents) + ') because your cart has ' + counted + ' eligible items' : head + ' (' + FREEHUB_PENDING + ')';
  }

  // Items Shopify counts toward LOAMLABS10 for a preconfigured build: the master line is not in the collection, so only the lines that are really in the cart and
  // eligible: kept valves, a PRICED freehub body (rule.freehubLine), optional add-ons and accessories. A $0 line is NOT counted: the store tests (pricing-data/
  // DISCOUNT_FIX_NOTES.md: 7 spokes + two $0 lines = no discount, 8 spokes + two $0 lines = discount) show Shopify ignores $0.00 lines. Conservative until a cart proves otherwise.
  function countedItems(components, optionalAddonCount, accessoryCount, rule) {
    var fh = rule && rule.freehubLine === true && components && components.rearComponentFreehub ? parseFloat(components.rearComponentFreehub.price) : 0;
    return keptValves(components).length + (fh > 0 ? 1 : 0) + (optionalAddonCount || 0) + (accessoryCount || 0);
  }

  global.PreconfiguredRules = { CFG: CFG, extrasMatch: extrasMatch, nipplesMatchLink: nipplesMatchLink, valvesExtra: valvesExtra, priceFromNode: priceFromNode, keptValves: keptValves, valveTotals: valveTotals, freehubSurcharge: freehubSurcharge, freehubText: freehubText, FREEHUB_PENDING: FREEHUB_PENDING, countedItems: countedItems, colorOf: colorOf };
})(typeof window !== 'undefined' ? window : globalThis);
